import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IItemProps } from "devextreme-react/form";

import { PageProps, UpdateDomTitleForForm } from "@ezhou/core";
import { MainCard } from "@ezhou/app_mantis_1_1";

import { tessituraInterfaceTypeOptions } from "../../model/constants/enums";
import { getUserProfileFromCache } from "../../services/userSettingService";

import { InactivatableCrudDataGrid } from "../common/InactivatableCrudDataGrid";

import { dummyTessituraSettingRow, tessituraInterfaceSettingColumns } from "./model/metaData";

const TessituraSettings = (pageProps: PageProps) => {
  const logger = pageProps.loggerProvider.getLogger("TessituraSettings");
  logger.debug("Rendering TessituraSettings component");

  useEffect(() => {
    UpdateDomTitleForForm(pageProps);
  });

  const organizationId = getUserProfileFromCache(pageProps.dataStore).defaultOrganization;
  const [restfulApiResources, setRestfulApiResources] = useState<string[]>([]);
  const [resourceScopesMap, setResourceScopesMap] = useState<Map<string, string[]>>(new Map());
  const [columnOptions, setColumnOptions] = useState<Map<string, IItemProps>>(
    () => new Map(Array.from(tessituraInterfaceSettingColumns.keys()).map(key => [key, {}]))
  );

  useEffect(() => {
    const fetchScopesForResource = async (resourceName: string): Promise<string[]> => {
      try {
        const response = await pageProps.appUtil.httpClients["eAccountingService"].
          post<{ scope: string }[], { parentId: { resourceName: string; organizationId: number } }>(
            `/restful-resources/scopes/get-all-by-parent-id`,
            { parentId: { resourceName, organizationId } },
            (err) => {
              logger.error(`Failed to fetch scopes for resource ${resourceName}`, err);
            },
          );
        if (response) {
          return response.map(r => r.scope);
        }
      } catch (error) {
        logger.error(`Error fetching scopes for resource ${resourceName}`, error);
      }
      return [];
    };
    const fetchRestfulApiResources = async () => {
      try {
        const response = await pageProps.appUtil.httpClients["eAccountingService"].
          post<{ name: string }[], { organizationId: number }>(
            `/restful-resources/get-all`,
            { organizationId },
            (err) => {
              logger.error("Failed to fetch restful API resources", err);
            },
          );
        if (response) {
          const resources = response.map(r => r.name);
          setRestfulApiResources(resources);

          // Build map from resource name to list of scope names
          const scopesMap = new Map<string, string[]>();
          await Promise.all(
            resources.map(async (resourceName) => {
              const scopes = await fetchScopesForResource(resourceName);
              scopesMap.set(resourceName, scopes);
            })
          );
          setResourceScopesMap(scopesMap);
        }
      } catch (error) {
        logger.error("Error fetching restful API resources", error);
      }
    };
    fetchRestfulApiResources();
  }, [organizationId, pageProps.appUtil.httpClients, logger]);

  const editorItemOptions = useMemo(() => new Map<string, IItemProps>([
    [
      "interfaceType",
      {
        editorType: "dxSelectBox",
        editorOptions: {
          dataSource: tessituraInterfaceTypeOptions,
          searchEnabled: true,
          placeholder: "Select an interface type",
        },
      },
    ],
    [
      "restfulApiResourceName",
      {
        editorType: "dxSelectBox",
        editorOptions: {
          items: restfulApiResources,
          searchEnabled: true,
          placeholder: "Select an API resource",
        },
      },
    ],
    [
      "restfulApiScopeName",
      {
        editorType: "dxSelectBox",
        editorOptions: {
          items: [],
          searchEnabled: true,
          placeholder: "Select a scope",
        },
      },
    ],
    [
      "procedureId",
      {
        editorType: "dxNumberBox",
      },
    ],
    [
      "active",
      {
        editorType: "dxCheckBox",
      },
    ],
  ]), [restfulApiResources]);

  const scopeEditorRef = React.useRef<any>(null);
  const onEditorPreparing = useCallback((e: any) => {
    // For popup mode, handle it differently
    if (e.parentType === "filterRow" || e.parentType === "searchPanel") return;

    if (e.dataField === "restfulApiScopeName") {
      const resouceName = e.row?.data?.restfulApiResourceName;
      e.editorOptions.disabled = !resouceName;
      e.editorOptions.dataSource = resourceScopesMap.get(resouceName) || [];

      // Store reference to the City editor when it's initialized
      e.editorOptions.onInitialized = (initArgs: any) => {
        scopeEditorRef.current = initArgs.component;
      };
      // Clear reference when editor is disposed
      e.editorOptions.onDisposing = () => {
        scopeEditorRef.current = null;
      };
    }else if(e.dataField === "interfaceType") {
      const interfaceFieldValue = e.value;
      if(interfaceFieldValue!==undefined && interfaceFieldValue!==null) {
        e.editorOptions.disabled = true;
      }else{
        e.editorOptions.disabled = false;
        e.editorOptions.readOnly = false;
      }
    }
  }, [resourceScopesMap]);
  const setCellValueForResource = useCallback((newData: any, value: any, currentRowData: any) => {
    newData.restfulApiResourceName = value;
    newData.restfulApiScopeName = null; // Reset scope when resource changes
    // Update scope editor data source
    if (scopeEditorRef.current) {
      scopeEditorRef.current.option("items", resourceScopesMap.get(value) || []);
      scopeEditorRef.current.option("value", null);
      scopeEditorRef.current.option("disabled", !value);
    }
  }, [resourceScopesMap]);
  useEffect(() => {
    const columnOptions = new Map<string, IItemProps>();
    Array.from(tessituraInterfaceSettingColumns.keys()).forEach((key) => {
      const options = { ...(tessituraInterfaceSettingColumns.get(key)) };
      if (key === "restfulApiResourceName") {
        options["setCellValue"] = setCellValueForResource;
      }
      columnOptions.set(key, options);
    });
    setColumnOptions(columnOptions);
  }, [setCellValueForResource]);

  return (
    <MainCard title="Tessitura Interface Settings">
      <InactivatableCrudDataGrid
        pageProps={pageProps}
        logger={logger}
        dummyRow={dummyTessituraSettingRow}
        baseUrl="tessitura/settings/interfaces"
        isOrganizationData={true}
        keyExpr="interfaceType"
        formTitle="Tessitura Setting"
        formColCount={2}
        formColSpan={2}
        allowAdding={true}
        allowUpdating={true}
        allowDeleting={true}
        columnConfigs={columnOptions}
        editorItemOptions={editorItemOptions}
        onEditorPreparing={onEditorPreparing}
      ></InactivatableCrudDataGrid>
    </MainCard>
  );
};

export default TessituraSettings;
